import { getRatingsFromSurveyScale } from "@repo/app-types/client/client";
import {
  type ComplexAnswer,
  isRatingSurveyResponseList,
  isSimpleSurveyResponse,
} from "@repo/app-types/entities/answer";
import type {
  ComplexInterviewQuestion,
  InterviewQuestion,
} from "@repo/app-types/entities/question";
import { isComplexInterviewQuestion } from "@repo/app-types/helpers/utilities";
import { RadioButtonsGroup } from "@repo/ui/Buttons/radioButtonsGroup";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { SurveyQuestionTable } from "@repo/ui/tables/surveyQuestionTable";
import type { FC } from "react";

interface SurveyResponsesSectionProps {
  interviewQuestions: InterviewQuestion[];
  complexAnswers: ComplexAnswer[];
}

export const SurveyResponsesSection: FC<SurveyResponsesSectionProps> = ({
  interviewQuestions,
  complexAnswers,
}) => {
  if (complexAnswers.length === 0) return null;

  // Display interview questions that have answers
  const surveyAnswersToDisplay = interviewQuestions
    .map((q) => {
      return (
        isComplexInterviewQuestion(q) &&
        complexAnswers.find((r) => r.question_id === q.id)
      );
    })
    .filter((q) => q !== undefined) as ComplexAnswer[];

  const renderResponseData = (
    question: ComplexInterviewQuestion,
    answer: ComplexAnswer,
  ) => {
    switch (question.type) {
      case "rating": {
        if (!isRatingSurveyResponseList(answer.response)) return null;

        return (
          <div className="flex flex-col space-y-4">
            <SurveyQuestionTable
              columnNames={getRatingsFromSurveyScale(question.scale).map(
                (r) => r.name,
              )}
              rowNames={answer.response.map((r) => r.name)}
              isReadOnly={true}
              currentRatings={answer.response}
            />
          </div>
        );
      }
      case "multiple_choice": {
        if (!isSimpleSurveyResponse(answer.response)) return null;

        return (
          <div>
            <RadioButtonsGroup
              options={question.options}
              value={answer.response.value}
              size={Size.MEDIUM}
              isDisabled
            />
          </div>
        );
      }
    }
  };

  return (
    <>
      <Label className="mt-2 block" size={Size.SMALL}>
        Survey responses
      </Label>
      <div className="mt-2 flex flex-col space-y-4">
        {surveyAnswersToDisplay.map((answer, index) => {
          const question = interviewQuestions.find(
            (q) => isComplexInterviewQuestion(q) && q.id === answer.question_id,
          );
          if (!question || !isComplexInterviewQuestion(question)) return null;

          return (
            <div key={`survey-answer-${answer.id}`}>
              <div className="mb-2 text-foreground">{question.question}</div>
              {renderResponseData(question, answer)}
            </div>
          );
        })}
      </div>
    </>
  );
};
